import { Component } from '@theme/component';
import { debounce, onDocumentLoaded } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

const ACTIVATE_DELAY = 0;
const DEACTIVATE_DELAY = 350;

/**
 * Check if user prefers reduced motion
 * @returns {boolean}
 */
const prefersReducedMotion = () =>
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.overflowMenu?.addEventListener('pointerleave', () => this.#debouncedDeactivate(), {
      signal: this.#abortController.signal,
    });

    onDocumentLoaded(this.#preloadImages);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * Time to allow for a closing animation between initiating a deactivation and actually deactivating the active item.
   * @returns {number}
   */
  get animationDelay() {
    const value = this.dataset.animationDelay;
    return value ? parseInt(value, 10) : 0;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow menu is hovered
   * @returns {boolean}
   */
  get overflowHovered() {
    return this.refs.overflowMenu?.matches(':hover') ?? false;
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    this.#debouncedDeactivate.cancel();
    this.#debouncedActivateHandler.cancel();

    this.#debouncedActivateHandler(event);
  };

  /**
   * Activate the selected menu item with a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #activateHandler = (event) => {
    this.#debouncedDeactivate.cancel();

    this.dispatchEvent(new MegaMenuHoverEvent());

    this.removeAttribute('data-animating');

    if (!(event.target instanceof Element)) return;

    let item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    const isDefaultSlot = event.target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';

      // Clean up the previous submenu immediately when switching between items
      const prevSubmenu = findSubmenu(previouslyActiveItem);
      if (prevSubmenu) {
        prevSubmenu.classList.remove('is-animating-in');
        delete prevSubmenu.dataset.active;
        prevSubmenu.style.opacity = '';
        prevSubmenu.style.transform = '';
        if (typeof anime !== 'undefined') {
          anime.remove(prevSubmenu);
          anime.remove(prevSubmenu.querySelectorAll('.mega-menu__column'));
        }
        prevSubmenu.querySelectorAll('.mega-menu__column').forEach((col) => {
          /** @type {HTMLElement} */ (col).style.opacity = '';
          /** @type {HTMLElement} */ (col).style.transform = '';
        });
      }
      previouslyActiveItem.removeAttribute('data-animating');
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);

    if (!submenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    // Check if the active submenu is a simple dropdown (flat menu)
    const isDropdown = submenu?.querySelector('.mega-menu__dropdown-list') != null;
    this.dataset.dropdownActive = isDropdown.toString();

    // Mark submenu as active for content-visibility optimization
    if (submenu) {
      submenu.dataset.active = '';
      submenu.classList.add('is-animating-in');

      // Trigger the Anime.js animation
      this.#animateSubmenuIn(submenu);
    }

    const submenuHeight = submenu ? submenu.offsetHeight : 0;

    this.style.setProperty('--submenu-height', isDropdown ? '0px' : `${submenuHeight}px`);
    this.style.setProperty('--submenu-opacity', isDropdown ? '0' : '1');
  };

  #debouncedActivateHandler = debounce(this.#activateHandler, ACTIVATE_DELAY);

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    this.#debouncedActivateHandler.cancel();

    if (!(event.target instanceof Element)) return;

    const item = findMenuItem(event.target);

    // Make sure the item to be deactivated is still the active one. Ideally
    // we cancelled the debounce before the item was changed, but just in case.
    if (item === this.#state.activeItem) {
      this.#debouncedDeactivate();
    }
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;
    if (this.overflowHovered) return;

    this.style.setProperty('--submenu-height', '0px');
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const submenu = findSubmenu(item);

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';
    item.setAttribute('data-animating', '');

    // Trigger the Anime.js close animation
    if (submenu) {
      submenu.classList.remove('is-animating-in');
      this.#animateSubmenuOut(submenu).then(() => {
        item.removeAttribute('data-animating');
        delete submenu.dataset.active;
      });
    } else {
      setTimeout(() => {
        item.removeAttribute('data-animating');
      }, Math.max(0, this.animationDelay - 150));
    }
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #debouncedDeactivate = debounce(this.#deactivate, DEACTIVATE_DELAY);

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  /**
   * Animate submenu in with "Soft Bounce Cascade" effect
   * @param {HTMLElement} submenu
   */
  #animateSubmenuIn = (submenu) => {
    const columns = submenu.querySelectorAll('.mega-menu__column');

    // Respect reduced motion preference
    if (prefersReducedMotion()) {
      submenu.style.opacity = '1';
      submenu.style.transform = 'none';
      columns.forEach((col) => {
        /** @type {HTMLElement} */ (col).style.opacity = '1';
        /** @type {HTMLElement} */ (col).style.transform = 'none';
      });
      return;
    }

    // Check if anime.js is available - if not, show instantly without animation
    if (typeof anime === 'undefined') {
      submenu.style.opacity = '1';
      submenu.style.transform = 'none';
      columns.forEach((col) => {
        /** @type {HTMLElement} */ (col).style.opacity = '1';
        /** @type {HTMLElement} */ (col).style.transform = 'none';
      });
      return;
    }

    // Kill any existing animations on these elements
    anime.remove(submenu);
    anime.remove(columns);

    // Reset initial states
    submenu.style.opacity = '0';
    submenu.style.transform = 'translateY(-8px)';
    columns.forEach((col) => {
      /** @type {HTMLElement} */ (col).style.opacity = '0';
      /** @type {HTMLElement} */ (col).style.transform = 'translateY(12px)';
    });

    // Soft Bounce Cascade animation
    anime
      .timeline({
        easing: 'easeOutQuad',
      })
      .add({
        targets: submenu,
        opacity: [0, 1],
        translateY: [-8, 0],
        duration: 200,
      })
      .add(
        {
          targets: columns,
          translateY: [12, 0],
          opacity: [0, 1],
          easing: 'easeOutBack',
          delay: anime.stagger(45, { from: 'center' }),
          duration: 280,
        },
        '-=100'
      );
  };

  /**
   * Animate submenu out
   * @param {HTMLElement} submenu
   * @returns {Promise<void>}
   */
  #animateSubmenuOut = (submenu) => {
    return new Promise((resolve) => {
      const columns = submenu.querySelectorAll('.mega-menu__column');

      // Respect reduced motion preference
      if (prefersReducedMotion()) {
        submenu.style.opacity = '0';
        columns.forEach((col) => {
          /** @type {HTMLElement} */ (col).style.opacity = '0';
        });
        resolve();
        return;
      }

      // Check if anime.js is available
      if (typeof anime === 'undefined') {
        resolve();
        return;
      }

      // Kill any existing animations
      anime.remove(submenu);
      anime.remove(columns);

      anime
        .timeline({
          easing: 'easeOutQuad',
          complete: () => resolve(),
        })
        .add({
          targets: columns,
          translateY: [0, 8],
          opacity: [1, 0],
          delay: anime.stagger(20, { from: 'center', direction: 'reverse' }),
          duration: 150,
        })
        .add(
          {
            targets: submenu,
            opacity: [1, 0],
            translateY: [0, -5],
            duration: 150,
          },
          '-=80'
        );
    });
  };
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
